export type CancellablePromise<T> = {
  promise: Promise<T>
  cancel: () => void
}

export class PromiseCancelledError extends Error {
  constructor() {
    super('Promise cancelled.')
    this.name = 'PromiseCancelledError'
  }
}

export const cancelablePromise = <T>(promise: Promise<T>): CancellablePromise<T> => {
  let hasCanceled = false

  const wrappedPromise: Promise<T> = new Promise((resolve, reject) => {
    promise.then(
      val => (hasCanceled ? reject(new PromiseCancelledError()) : resolve(val)),
      error => (hasCanceled ? reject(new PromiseCancelledError()) : reject(error))
    )
  })

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled = true
    },
  }
}

export const ensureCancelablePromise = <T>(
  promise: Promise<T> | CancellablePromise<T>
): CancellablePromise<T> => {
  if ('cancel' in promise) {
    return promise
  }

  return cancelablePromise(promise)
}

export const singletonPromise = <T>() => {
  let storedPromise: {promise: Promise<T>; cancel: () => void}

  return (promise: Promise<T>): Promise<T> => {
    // Cancel old promise
    if (storedPromise) {
      storedPromise.cancel()
    }

    storedPromise = cancelablePromise(promise)

    return storedPromise.promise
  }
}

export const waitPromise = (delay: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(resolve, delay)
  })

export const retryPromise = <T>(
  fn: () => Promise<T> | CancellablePromise<T>,
  times: number,
  delay = 0,
  shouldRetry: (error: Error) => boolean = () => true,
  onRetry: (error: Error) => void = () => undefined
): CancellablePromise<T> => {
  let isCancelled = false
  let innerCancel: (() => void) | null = null

  const promise = new Promise<T>((resolve, reject) => {
    const attempt = (retries: number) => {
      const {promise: innerPromise, cancel} = ensureCancelablePromise(fn())

      innerCancel = cancel

      innerPromise.then(resolve).catch(error => {
        if (isCancelled) {
          reject(new PromiseCancelledError())
          return
        }

        if (retries <= 0 || error instanceof PromiseCancelledError || !shouldRetry(error)) {
          reject(error)
          return
        }

        onRetry(error)
        setTimeout(() => attempt(retries - 1), delay)
      })
    }
    attempt(times)
  })

  return {
    promise,
    cancel: () => {
      isCancelled = true
      innerCancel?.()
    },
  }
}
